import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Scan,
  Printer,
  Server,
  Network,
  Workflow,
  Boxes,
  Brain,
  ShieldCheck,
  Building2,
  Cpu,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Scan,
    title: 'WEC is the scan front door',
    detail:
      'WebCenter Enterprise Capture is the browser-based scan and document import client + server tier that sits between your physical scanners and the rest of the Oracle WebCenter stack. Batches enter here, get classified and indexed, and commit to WebCenter Content (WCC) as the system of record.',
  },
  {
    icon: Boxes,
    title: 'WEC succeeded ODC and ODDC',
    detail:
      'Oracle Document Capture (ODC) and Oracle Distributed Document Capture (ODDC) were the prior-generation desktop capture products. WEC consolidated both into a single browser-based client/server architecture. WEC Standard Edition (WEC SE) is the Oracle-documented upgrade path for ODC/ODDC customers who have not yet moved.',
  },
  {
    icon: Workflow,
    title: 'WEC hands off to WFR and WCC',
    detail:
      'WEC handles batch acquisition, scanning, image processing, and operator indexing. Classification and field-level extraction are then handed off to WebCenter Forms Recognition (WFR), and the final committed document lands in WebCenter Content (WCC). The three products are typically deployed together as one AP capture pipeline.',
  },
  {
    icon: Printer,
    title: 'Your scanner fleet is part of the decision',
    detail:
      'Kodak, Fujitsu (Ricoh), Canon, and other high-volume production scanners are wired into WEC via ISIS and TWAIN drivers. The modernization decision has to preserve that physical hardware investment — no realistic forward path requires replacing the scanner fleet.',
  },
];

const ARCHITECTURE = [
  {
    icon: Network,
    title: 'Client tier — browser-based capture client',
    detail:
      'WEC moved away from the ODDC desktop-installer pattern to a Java-applet-then-browser-based client that scan operators run from their workstations. Hooks into ISIS scanner drivers (and TWAIN where ISIS is unavailable) so existing Kodak / Fujitsu / Canon production scanners keep working.',
  },
  {
    icon: Server,
    title: 'Server tier — WebLogic-deployed capture server',
    detail:
      'The WEC server runs as a managed server in the WebLogic domain alongside WebCenter Content and WebCenter Imaging. Holds capture configurations (workspaces, batches, processors, commit profiles) and orchestrates the capture pipeline. Database metadata lives in the same schema family as the rest of WebCenter.',
  },
  {
    icon: Workflow,
    title: 'Batch processing — image, classify, index, commit',
    detail:
      'A batch flows through configurable processors — image quality, blank-page detection, barcode recognition, document separation, classification, indexing, and finally a commit profile that posts to WCC. Classification and field-level extraction handoff to WebCenter Forms Recognition is one of the standard processor patterns.',
  },
  {
    icon: Boxes,
    title: 'Commit to WebCenter Content as the system of record',
    detail:
      'The end state of a WEC batch is one or more documents checked into WCC with metadata. From there, the AP workflow (often via SOA composites or AME) routes the document for coding, approval, and posting back to EBS or Fusion.',
  },
];

const STAKES = [
  {
    icon: Printer,
    title: 'Physical scanner fleet',
    detail:
      'Production AP scan rooms typically have one or more high-volume scanners (Kodak i-series, Fujitsu fi-series, Canon DR-series) feeding WEC via ISIS drivers. Replacement cost runs into tens of thousands per device — and operator retraining costs sit on top. A WEC modernization that requires new scanners is a different project than one that does not.',
  },
  {
    icon: Building2,
    title: 'Scan operator workflow and training',
    detail:
      'Scan operators have learned a specific UI — batch creation, prep, scan, image cleanup, indexing, commit. In distributed-capture configurations, remote-office operators feed central QC. Replacing the operator UI is a real change-management event, not just a UI swap.',
  },
  {
    icon: Workflow,
    title: 'Throughput on month-end close',
    detail:
      'WEC throughput on the last business day of the month is often the bottleneck that defines whether AP closes on time. Any forward path has to preserve or improve the peak-day batch rate — not just the average-day rate.',
  },
  {
    icon: Network,
    title: 'Integration with WFR and WCC',
    detail:
      'WEC sits in the middle of a three-product capture pipeline. Whichever direction you go on WEC, the handoff to WFR (extraction) and WCC (repository) has to keep working — or those two products move too. The decision is rarely WEC-only.',
  },
];

const PATHS = [
  {
    label: 'Path 1',
    title: 'Upgrade WEC to 14c with the rest of WebCenter',
    body: 'WEC is included in WebCenter Content 14.1.2. The 12c → 14c upgrade for WEC follows the same out-of-place domain directory upgrade pattern as the rest of the WebCenter stack — a new 14c domain is created alongside the 12c domain, capture workspaces and commit profiles are migrated forward, and ISIS / TWAIN scanner connectivity is re-validated. Existing scanner fleet preserved, existing operator workflow preserved.',
    suited:
      'Stack is close to Oracle reference architecture, WFR + WCC are also being upgraded to 14c, and the AP team is not asking for a new operator UI.',
  },
  {
    label: 'Path 2',
    title: 'Layer AI capture on top of WEC',
    body: 'Keep WEC as the scan front door — the operator UI, the scanner drivers, the batch processing — but reroute the classification and extraction stages from WFR templates to a continuous-learning AI extraction layer. WEC continues to acquire batches from existing scanners; the AI layer replaces template-rules-per-vendor maintenance. Commit still lands in WCC. Lowest-disruption forward path for shops where the operator workflow is fine but WFR template maintenance has become the burden.',
    suited:
      'Operator workflow is working but WFR rule maintenance is the actual pain point, and there is appetite to reduce vendor-template-per-vendor maintenance overhead.',
  },
  {
    label: 'Path 3',
    title: 'Replace WEC with a modern AI capture pipeline',
    body: 'Replace the WEC client/server tier with a modern scan integration layer — keeping the physical scanner fleet — and run classification, extraction, indexing, and commit through an AI-native capture pipeline that posts directly into Oracle EBS or Fusion. Scanners stay; the WEC operator UI and the WFR templating layer go. Strategic forward path for shops that want to make the capture-platform decision once.',
    suited:
      'AP team is ready for a modern operator UI and a single integrated AI capture pipeline, and the broader WebCenter retirement decision is on the table.',
  },
];

const SCENARIOS = [
  {
    title: 'Still on ODC or ODDC, never migrated to WEC',
    body: 'A meaningful share of long-running Oracle AP shops never moved off Oracle Document Capture (ODC) or Oracle Distributed Document Capture (ODDC). The desktop client still works, the scanners still scan, and the migration to WEC SE keeps getting deferred. With 12c support closing in December 2026, the ODC/ODDC question converges with the WEC modernization question — and skipping straight to a forward path may be more efficient than the WEC SE step.',
  },
  {
    title: 'WEC throughput hitting a ceiling on month-end',
    body: 'Day-to-day batch volume runs fine. The last two business days of the month overflow the capture pipeline and AP closes late. The scanners are not the bottleneck — they sit idle while operators wait on indexing or while batches queue at the WFR handoff. Modernization options vary in how directly they address peak-day throughput.',
  },
  {
    title: 'Scan operators want a modern web UI or mobile capture',
    body: 'WEC is browser-based but its operator UI dates from the early 2010s. Scan operators — and remote-office staff sending in batches from a multi-function device — increasingly expect a phone-photo capture option, drag-and-drop ingest, and a modern review UI. Some forward paths add this without removing WEC; others replace WEC outright.',
  },
  {
    title: 'Adding multi-function-device capture without changing scanners',
    body: 'A common ask: keep the production scanners in the AP scan room, but also let regional offices and remote staff submit invoices from the office multi-function-device (Xerox, Canon, Ricoh, HP) without operator intervention. WEC supports MFD capture in principle; in practice this is where customizations and operator training have layered up over the years.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    eyebrow: 'Tier 1 · WEC Decision Assessment',
    title: 'Fixed-fee read of your scan and capture pipeline',
    body: 'Andrew reads your existing WEC (or ODC / ODDC) implementation directly — workspaces, batch processors, commit profiles, scanner driver configuration, WFR handoff, WCC commit pattern — and delivers a forward-path decision document. Surfaces every scanner model in the fleet, every distributed-capture remote operator, and every customization that needs to be carried forward. Output is yours to keep regardless of which delivery partner you ultimately choose.',
  },
  {
    eyebrow: 'Tier 2 · Forward Deployed Engineering',
    title: 'Founder-level engineer embedded in your WEC modernization',
    body: 'For shops where the WEC implementation has years of accumulated customization — custom processors, custom commit profiles, custom scanner integration, undocumented operator workflow — the assessment tier is not enough. FDE means Andrew works directly inside your environment, reads the running system, and produces the migration plan against the actual implementation rather than against assumed-standard documentation. Scoped per situation on a Decision Call.',
  },
  {
    eyebrow: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the WEC decision window',
    body: 'Monthly retainer with Andrew through the 6-12 month decision and execution window. Strategic guidance on path selection, scanner-fleet preservation strategy, distributed-capture operator-workflow changes, WFR-handoff implications, and 14c-specific scan-server considerations. Sized for the customer working through internal stakeholders, procurement, and Oracle in parallel.',
  },
  {
    eyebrow: 'Tier 4 · Targeted WEC project work',
    title: 'Project-based scoped outcomes',
    body: 'ODC / ODDC to WEC migration audits, WEC 12c → 14c upgrade planning, ISIS / TWAIN scanner-driver compatibility review, distributed-capture re-architecture, multi-function-device capture integration, WFR-handoff rewire to an AI extraction layer, and WEC-to-modern-capture replacement planning. Project-based, founder-led, scoped to a defined outcome.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle WebCenter Enterprise Capture (WEC) dead?',
    a: 'No — WEC is included in WebCenter Content 14.1.2 and continues to be a supported component of the Oracle WebCenter product family. The forcing function is not WEC itself — it is that 12c Fusion Middleware Premier Support ends December 2026, which moves every 12c WEC customer into a modernization decision. WEC on 14c remains a legitimate forward path.',
  },
  {
    q: 'What is the ODC / ODDC migration path to WEC?',
    a: 'Oracle\'s documented migration path for Oracle Document Capture (ODC) and Oracle Distributed Document Capture (ODDC) customers is WEC Standard Edition (WEC SE). With the December 2026 12c support cliff approaching, many ODC / ODDC shops are converging the WEC SE step with the broader 12c modernization decision rather than doing both sequentially. The right answer depends on the age and customization depth of the existing ODC / ODDC implementation.',
  },
  {
    q: 'Does AI capture replace WEC entirely, or does it sit on top?',
    a: 'Both patterns are valid forward paths — AI capture can sit on top of WEC, or it can replace WEC end-to-end. In the layered pattern, WEC keeps the scan operator workflow and the scanner driver integration, and the AI layer replaces only the WFR template-extraction stage. In the replacement pattern, modern scan integration feeds directly into the AI pipeline with no WEC client or server tier. Path 2 versus Path 3 in the framework above — the right answer depends on how much of WEC\'s operator workflow you actually want to keep.',
  },
  {
    q: 'What about our existing scanner drivers — do they need to be replaced?',
    a: 'No realistic forward path requires replacing the physical scanner fleet. Kodak i-series, Fujitsu (Ricoh) fi-series, Canon DR-series, and similar production scanners ship with ISIS and TWAIN drivers that any serious capture platform supports. A WEC modernization plan should explicitly preserve scanner-fleet compatibility — and any vendor that asks you to retire working production scanners is proposing a different project than the one you should be running.',
  },
  {
    q: 'Can WEC run on Linux?',
    a: 'Yes — WEC server-tier components run on the same OS targets as the WebCenter Content domain they are deployed into, which includes Oracle Linux and other supported Linux distributions on the certified WebLogic / Fusion Middleware matrix. The browser-based capture client runs cross-platform on operator workstations. ISIS scanner driver availability is the constraint to validate, since scanner drivers are vendor-specific.',
  },
  {
    q: 'How does WEC compare to OCI Document Understanding capture?',
    a: 'WEC and OCI Document Understanding solve different problems and are typically layered together rather than compared. WEC is a scan / batch acquisition + operator-driven indexing pipeline that commits to WCC as a content repository. OCI Document Understanding is an OCI AI service that extracts structured fields from documents — closer to the WFR layer than to the WEC layer. Layering OCI Document Understanding (or another AI extraction service) on top of WEC is one of the live forward-path patterns (Path 2 above), where WEC keeps doing scan and operator workflow and the AI service replaces template-based extraction.',
  },
  {
    q: 'How long does a typical WEC modernization take?',
    a: 'Typical WEC modernizations run 3 to 12 months depending on which of the three forward paths is chosen. Path 1 (WEC 12c → 14c upgrade) typically runs 4-9 months as part of the broader WebCenter 14c upgrade, gated by scanner-driver re-validation and operator UAT. Path 2 (AI capture layered on top of WEC) typically runs 3-6 months because the operator workflow does not change. Path 3 (WEC replacement) typically runs 6-12 months because both operator workflow and scanner integration are rebuilt. The Decision Assessment tier surfaces the right timeline for the specific implementation.',
  },
  {
    q: 'Are there hybrid options — keep WEC in one site, modernize elsewhere?',
    a: 'Yes — hybrid configurations are common, especially in distributed-capture topologies. A central AP scan room may keep WEC + production scanners for high-volume daily batches while remote offices move to a modern web / mobile capture path that commits into the same downstream WCC repository. This split runs cleanly because WCC is the system of record on both sides; what changes is the front-door acquisition pattern per site.',
  },
];

const ModernizeWebCenterEnterpriseCapture = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Modernize Oracle WebCenter Enterprise Capture (WEC) — Replace, Upgrade, or AI-augment | EZ Cloud',
    description:
      'Oracle WebCenter Enterprise Capture (WEC) is the scan-front-door of every paper-heavy AP shop. WEC replaced Oracle Document Capture (ODC) and Oracle Distributed Document Capture (ODDC). With Fusion Middleware 12c support ending December 2026, the WEC modernization decision affects scan operators, AP supervisors, and your physical scanner fleet. Three forward paths — 14c upgrade, AI-augmented capture, or full replacement — all without rip-and-replacing your scanners.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/modernize-webcenter-enterprise-capture',
    },
    about: [
      'Oracle WebCenter Enterprise Capture',
      'WEC',
      'Oracle Document Capture',
      'ODC',
      'Oracle Distributed Document Capture',
      'ODDC',
      'Document Scanning',
      'Accounts Payable Automation',
    ].map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Modernize Oracle WebCenter Enterprise Capture (WEC) — Replace, Upgrade, or AI-augment | EZ Cloud"
        description="Oracle WebCenter Enterprise Capture (WEC) is the scan-front-door of every paper-heavy AP shop. WEC replaced Oracle Document Capture (ODC) and Oracle Distributed Document Capture (ODDC). With Fusion Middleware 12c support ending December 2026, the WEC modernization decision affects scan operators, AP supervisors, and your physical scanner fleet. Three forward paths — 14c upgrade, AI-augmented capture, or full replacement — all without rip-and-replacing your scanners."
        canonical="https://ezcloud.ai/solutions/modernize-webcenter-enterprise-capture"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Modernize WebCenter Enterprise Capture',
            url: 'https://ezcloud.ai/solutions/modernize-webcenter-enterprise-capture',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Scan className="w-4 h-4" aria-hidden />
              WebCenter Enterprise Capture · WEC · Scan workflow
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Modernize Oracle WebCenter Enterprise Capture
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Replace, upgrade to 14c, or layer AI capture on top — without replacing your
                scanners.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              WebCenter Enterprise Capture (WEC) is the scan and document import layer that feeds
              the rest of the Oracle WebCenter AP stack. It succeeded Oracle Document Capture (ODC)
              and Oracle Distributed Document Capture (ODDC). Three forward paths, all preserving
              the physical scanner fleet.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wec-modernize-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WEC Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle WebCenter Enterprise Capture, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter Enterprise Capture (WEC) is the scan and document import layer of
                  the Oracle WebCenter AP stack.
                </strong>{' '}
                It is a client/server product — a browser-based capture client running on scan
                operator workstations, plus a WebLogic-deployed capture server — that acquires
                batches from physical scanners (via ISIS and TWAIN drivers), runs them through
                configurable image, classification, and indexing processors, and commits the
                resulting documents to WebCenter Content (WCC) as the system of record.
              </p>
              <p>
                <strong className="text-gray-900">
                  WEC succeeded Oracle Document Capture (ODC) and Oracle Distributed Document
                  Capture (ODDC).
                </strong>{' '}
                ODC was the desktop-installer scan client of the late 2000s; ODDC added
                remote-office and central-QC patterns on top. Oracle consolidated both into WEC.
                For customers still running ODC or ODDC, WEC Standard Edition (WEC SE) is the
                Oracle-documented upgrade path.
              </p>
              <p>
                With Oracle Fusion Middleware 12c Premier Support ending{' '}
                <strong className="text-gray-900">December 2026</strong>, every 12c WEC customer
                has three forward paths: upgrade WEC to 14c (it ships inside WebCenter Content
                14.1.2), layer AI capture on top of WEC to replace the WFR template-extraction
                stage while keeping the operator workflow, or replace WEC end-to-end with a modern
                AI capture pipeline — all without retiring the physical scanner fleet.
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What WEC actually is
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four facts that frame the WEC modernization decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              WEC is one product in a three-product capture pipeline — and the modernization
              decision touches the scanner fleet, the operator workflow, and the downstream
              extraction and content tiers.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Architecture */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              WEC architecture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Client + server + batch pipeline + commit
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The WEC architecture is well-defined and well-documented. Understanding the four
              pieces is the prerequisite to choosing a forward path that preserves what matters and
              changes what does not.
            </p>
            <div className="space-y-5">
              {ARCHITECTURE.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent flex-shrink-0">
                      <Icon className="w-6 h-6" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                      <p className="text-gray-700 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's at stake */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's at stake
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four assets a WEC modernization has to preserve
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The WEC decision is not abstract. Each forward path interacts with a specific set of
              physical, operational, and integration assets — and the right answer depends on which
              of these the AP team needs to keep intact.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {STAKES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Three forward paths */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Three forward paths
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Upgrade WEC, layer AI on top, or replace WEC end-to-end
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              All three preserve the physical scanner fleet. They differ in how much of WEC's
              operator workflow, scan-server tier, and WFR-handoff pattern is preserved versus
              replaced. See the broader{' '}
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="text-accent font-semibold hover:underline"
              >
                12c end-of-support decision frame
              </Link>{' '}
              for how this fits the larger WebCenter window.
            </p>
            <div className="space-y-5">
              {PATHS.map((p) => (
                <Card key={p.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {p.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{p.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">{p.body}</p>
                  <p className="text-sm text-gray-600 italic">
                    <span className="font-semibold not-italic text-gray-900">Best suited for: </span>
                    {p.suited}
                  </p>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Read the 14c upgrade detail
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
              <Link
                to="/solutions/modernize-webcenter-forms-recognition"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the WFR modernization page
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Scanner fleet preservation */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-accent/5 border border-accent/30 shadow-sm">
              <div className="flex items-start gap-4">
                <Printer className="w-6 h-6 text-accent flex-shrink-0 mt-1" aria-hidden />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    Explicit promise
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    No scanner replacement required on any of the three forward paths
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Kodak i-series, Fujitsu (Ricoh) fi-series, Canon DR-series, and similar
                    production scanners are universally supported through ISIS and TWAIN drivers.
                    Multi-function-device capture from Xerox, Canon, Ricoh, and HP units is handled
                    via standard scan-to-folder or scan-to-email patterns plus a watch-folder
                    ingest. A modernization plan should explicitly preserve scanner-fleet
                    compatibility — that constraint comes first, the platform decision comes
                    second.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    What changes between paths is the scan-server tier (WEC 12c vs WEC 14c vs a
                    modern scan integration layer), the operator UI (legacy WEC client vs the same
                    WEC client on 14c vs a modern web/mobile UI), and the WFR-handoff (template
                    extraction vs continuous-learning AI extraction). The scanners themselves do
                    not move.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Distributed Capture scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Distributed Capture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Remote-office capture and hub-and-spoke processing
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Many WEC implementations grew out of ODDC — Oracle Distributed Document Capture —
              and still run hub-and-spoke. Remote operators scan locally, batches flow to a
              central server, and central QC handles indexing and commit. The forward path has to
              respect that topology.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <Building2 className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-gray-900 font-semibold mb-2">Remote-office scan operators</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Regional or branch offices with a local scanner and a single operator who scans
                  daily mail. Throughput per site is low; total volume across sites is meaningful.
                  Forward path needs a lightweight remote-operator UI, not the full central
                  operator UI.
                </p>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <Network className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-gray-900 font-semibold mb-2">Hub-and-spoke processing</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Batches scanned at the spokes flow to the central server for classification,
                  extraction, and commit. The hub holds the WFR templates and the WCC repository.
                  Network latency and batch-handoff reliability are the operational concerns to
                  preserve across the forward-path decision.
                </p>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-gray-900 font-semibold mb-2">Central QC</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A small central indexing and quality-control team reviews remote batches, fixes
                  classification errors, and approves the commit to WCC. The QC UI is one of the
                  most-customized parts of a long-running WEC implementation and needs explicit
                  attention on any forward path.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Common WEC scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Common WEC scenarios on inbound calls
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the four most common situations on Decision Calls with Oracle AP shops on
              WEC, ODC, or ODDC. The forward-path conversation usually opens here.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map((s) => (
                <Card key={s.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{s.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on WEC modernization
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers — Standard Consulting and Forward Deployed Engineering
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level Oracle WebCenter expertise that powers EZ Cloud's
              modernization assessments applies to the WEC decision. Standard Consulting is the
              default; Forward Deployed Engineering is available when the implementation has
              accumulated enough customization that an assessment from outside the running system
              is not enough.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.eyebrow} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the forward paths across the full WebCenter AP stack — WEC,
              WFR, WCC, Imaging — with stack-specific considerations, diagnostic questions, and
              vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WEC modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your WEC modernization
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with 20+ years specifically inside the Oracle WebCenter stack —
              including Capture (ODC), Enterprise Capture (WEC), Forms Recognition (WFR),
              Imaging (IPM), and Content (UCM). Walk through your scanner fleet, your operator
              workflow, and the forward path in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wec-modernize-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WEC Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModernizeWebCenterEnterpriseCapture;
